import { LocalizationProvider, ReactLocalization } from "@fluent/react"
import { FluentBundle, FluentResource } from "@fluent/bundle"
import { ReactNode, useEffect, useState } from "react"
import { useAppSelector } from "./hooks"
import { Locale, selectSettings } from "../features/settings/settingsSlice"
import { Empty } from "../Empty"

interface AppLocalizationProviderProps {
    children: ReactNode;
}

export const loadMessages = async (locale: Locale) => {
    const response = await fetch(`${process.env.PUBLIC_URL}/l10n/${locale}.ftl`, {
        cache: "no-cache",
    });
    if (!response.ok) {
        throw new Error(`Failed to load ${locale} translations (${response.status})`);
    }
    const messages = await response.text();
    const resource = new FluentResource(messages);
    const bundle = new FluentBundle(locale);
    bundle.addResource(resource);

    return new ReactLocalization([bundle]);
}

export const AppLocalizationProvider = (props: AppLocalizationProviderProps) => {
    const [localization, setLocalization] = useState<ReactLocalization | undefined>(undefined);
    const [error, setError] = useState<Error | null>(null);
    const { locale } = useAppSelector(selectSettings);

    useEffect(() => {
        let cancelled = false;
        setLocalization(undefined);
        setError(null);
        document.documentElement.lang = locale;
        loadMessages(locale).then(
            value => { if (!cancelled) setLocalization(value); },
            reason => {
                if (!cancelled) {
                    console.error(`Failed to load ${locale} translations`, reason);
                    setError(reason instanceof Error ? reason : new Error(String(reason)));
                }
            },
        );
        return () => { cancelled = true; };
    }, [locale]);

    if (error) throw error;
    if (localization === undefined) return (<Empty />);

    return ( <LocalizationProvider l10n={localization}>{props.children}</LocalizationProvider>);
} 